import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { fmtStamp } from '@/utils/dateFmt';
import { DateField } from '@/components/ui/date-field';

const API = process.env.REACT_APP_BACKEND_URL;
const th = 'text-left text-[10px] uppercase tracking-wider text-brand-muted font-medium px-3 py-2';

export default function AccessLogsPage() {
  useRequireAdmin();
  const [data, setData] = useState(null);
  const [f, setF] = useState({ user_id: '', patient: '', action: '', date_from: '', date_to: '' });
  const [page, setPage] = useState(1);
  const params = useCallback(() => Object.fromEntries(Object.entries(f).filter(([, v]) => v)), [f]);
  const load = useCallback(() => axios.get(`${API}/api/admin/access-logs`, { params: { ...params(), page } }).then(r => setData(r.data)).catch(() => toast.error('Erro ao carregar o registo')), [params, page]);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => { setPage(1); setF({ ...f, [k]: v }); };
  const exportCsv = async () => {
    const r = await axios.get(`${API}/api/admin/access-logs/export`, { params: params(), responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = url; a.download = 'registo_acessos.csv'; a.click(); URL.revokeObjectURL(url);
  };
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const sel = 'text-xs border border-brand-border rounded-lg px-2 py-2 bg-white';
  return (
    <div className="space-y-4" data-testid="access-logs-page">
      <div><h2 className="font-heading font-medium text-xl text-brand-text">Registo de acessos</h2>
        <p className="text-xs text-brand-muted">Quem abriu fichas e ficheiros clínicos de pacientes, e quando. Guardado durante 2 anos.</p></div>
      <div className="flex flex-wrap gap-2 items-end">
        <select value={f.user_id} onChange={e => set('user_id', e.target.value)} className={sel} data-testid="al-filter-user">
          <option value="">Todas as nutricionistas</option>{(data?.staff || []).map(s => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}</select>
        <Input value={f.patient} onChange={e => set('patient', e.target.value)} placeholder="Paciente" className="w-44 rounded-lg border-brand-border" data-testid="al-filter-patient" />
        <select value={f.action} onChange={e => set('action', e.target.value)} className={sel} data-testid="al-filter-action">
          <option value="">Todas as ações</option>{Object.entries(data?.actions || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <DateField value={f.date_from} onChange={e => set('date_from', e.target.value)} className={sel} data-testid="al-filter-from" />
        <DateField value={f.date_to} onChange={e => set('date_to', e.target.value)} className={sel} data-testid="al-filter-to" />
        <button onClick={exportCsv} className="ml-auto bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium" data-testid="al-export-csv">Exportar CSV</button>
      </div>
      {!data ? <p className="text-xs text-brand-muted">A carregar...</p> : !data.rows.length ? (
        <p className="text-sm text-brand-muted bg-white border border-brand-border rounded-2xl p-4" data-testid="al-empty">Sem acessos registados para estes filtros.</p>
      ) : (
        <div className="bg-white border border-brand-border rounded-2xl overflow-x-auto" data-testid="al-table">
          <table className="w-full text-sm"><thead className="bg-brand-bg"><tr>{['Data e hora', 'Utilizador', 'Paciente', 'Ação', 'Recurso'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>{data.rows.map(r => (
              <tr key={r.id} className="border-t border-brand-border/50" data-testid={`al-row-${r.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{fmtStamp(r.at)}</td><td className="px-3 py-2">{r.user_name}</td><td className="px-3 py-2">{r.patient_name}</td>
                <td className="px-3 py-2">{data.actions[r.action] || r.action}</td><td className="px-3 py-2 text-xs text-brand-muted break-all">{r.resource}</td></tr>))}</tbody></table>
        </div>
      )}
      {data && data.total > data.page_size && (
        <div className="flex items-center gap-3 text-xs" data-testid="al-pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="border border-brand-border rounded-full px-3 py-1 disabled:opacity-40" data-testid="al-prev">Anterior</button>
          <span>Página {page} de {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="border border-brand-border rounded-full px-3 py-1 disabled:opacity-40" data-testid="al-next">Seguinte</button>
        </div>
      )}
    </div>
  );
}
